import { gql } from '@apollo/client';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { ProductInstanceStatus } from '@mfe/shared/schema-types';

import { selectUser } from '../auth';
import { FetchWithErrorsQuery, graphqlQueryWithErrors } from '../graphql';
import {
  fetchHistoricalUsageInfo,
  fetchUnleashedUsage,
  FetchUnleashedUsageAction,
  selectHistoricalUsageInfo,
  setApiErrors,
  setHistoricalUsageInfo,
  setUnleashedUsage,
  setUnleashedUsageNotLoading,
} from './historicalUsageInfoSlice';

import type { UsageData } from './utils';
import { waitForToken } from '../utils/utilsSagas';

export const GET_HISTORICAL_USAGE = gql`
  query getHistoricalUsageByBillCycle($refetchData: Boolean) {
    getHistoricalUsageByBillCycle(refetchData: $refetchData) {
      billCycles {
        startDate
        endDate
        usageData {
          date
          usage
        }
      }
    }
  }
`;

const GET_HISTORICAL_USAGE_BY_DATE = gql`
  query getHistoricalUsageByDate(
    $refetchData: Boolean
    $start: String
    $end: String
  ) {
    getHistoricalUsageByDate(
      refetchData: $refetchData
      start: $start
      end: $end
    ) {
      intervalStart
      intervalEnd
      usageData {
        date
        usage
      }
    }
  }
`;

export function* fetchHistoricalUsageByBillCycle() {
  const {
    errors: hasApiErrorOccurred,
  }: ReturnType<typeof selectHistoricalUsageInfo> = yield select(
    selectHistoricalUsageInfo
  );

  const {
    user: { productInstanceStatus },
  } = yield select(selectUser);

  const refetchData = Boolean(hasApiErrorOccurred);

  yield call(waitForToken);

  if (productInstanceStatus === ProductInstanceStatus.Preinstall) {
    return;
  }

  const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_HISTORICAL_USAGE,
    variables: {
      refetchData,
    },
    fetchPolicy: refetchData ? 'no-cache' : 'cache-first',
  });

  const { data, errors } = apiResponse;
  if (errors) {
    yield put(setApiErrors(errors));
  }

  if (data?.getHistoricalUsageByBillCycle) {
    yield put(setHistoricalUsageInfo(data.getHistoricalUsageByBillCycle));
  }
}

function* fetchHistoricalUsageByDate(action: FetchUnleashedUsageAction) {
  const { unleashedUsage, errors: existingErrors } = yield select(
    selectHistoricalUsageInfo
  );
  const {
    user: { productInstanceStatus },
  } = yield select(selectUser);

  if (productInstanceStatus === ProductInstanceStatus.Preinstall) {
    yield put(setUnleashedUsageNotLoading());
    return;
  }

  const hasDataForSelectedInterval =
    unleashedUsage[action.payload.selectedInterval];

  const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_HISTORICAL_USAGE_BY_DATE,
    variables: {
      refetchData: !hasDataForSelectedInterval,
      start: action.payload.start,
      end: action.payload.end,
    },
    fetchPolicy: existingErrors ? 'network-only' : 'cache-first',
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError || errors) {
    yield put(setApiErrors(runtimeError ?? errors));
    return;
  }

  if (data?.getHistoricalUsageByDate) {
    yield put(
      setUnleashedUsage({
        usageData:
          (data.getHistoricalUsageByDate.usageData as UsageData[]) ?? [],
        interval: action.payload.selectedInterval,
      })
    );
  }
}

export function* watchHistoricalUsageInfo() {
  yield all([
    takeLatest(fetchHistoricalUsageInfo.type, fetchHistoricalUsageByBillCycle),
    takeLatest(fetchUnleashedUsage.type, fetchHistoricalUsageByDate),
  ]);
}
